import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import Hero from "../components/Hero";
import SectionHeader from "../components/SectionHeader";
import NewsCard from "../components/NewsCard";
import CtaSection from "../components/CtaSection";
import TraegervereineGrid from "../components/TraegervereineGrid";
import { fetchNeuesteNews } from "../cms/news";
import { fetchSiteSettings } from "../cms/siteSettings";
import { fetchTraegervereine } from "../cms/traegervereine";

const werte = [
  {
    titel: "Sportliche Exzellenz",
    text: "Dreimal wöchentlich Training, professionelle Betreuung und höchste Wettbewerbsstufen.",
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
  },
  {
    titel: "Teamgeist & Charakter",
    text: "Wir fördern nicht nur Fussballer, sondern formen verantwortungsvolle Persönlichkeiten.",
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
  {
    titel: "Regionale Verwurzelung",
    text: "5 Trägervereine rund um den Sempachersee – gemeinsam stark in der Region.",
    icon: (
      <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth="1.8" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
      </svg>
    ),
  },
];

export default function Home() {
  const [latestNews, setLatestNews]   = useState([]);
  const [settings, setSettings]       = useState(null);
  const [traegervereine, setTraeger]  = useState([]);

  useEffect(() => {
    fetchSiteSettings().then(setSettings);
    fetchNeuesteNews(3).then(setLatestNews);
    fetchTraegervereine().then(setTraeger);
  }, []);

  // Nur das im CMS gepflegte Hero-Bild anzeigen — kein Hardcode-Fallback.
  const heroBgImage = settings?.heroBild || undefined;

  return (
    <>
      <Seo
        title="Home"
        description="Team Sempachersee — Nachwuchsfussball der Spitzenklasse rund um den Sempachersee."
        path="/"
      />

      <Hero
        eyebrow={settings?.heroEyebrow  || "Nachwuchsfussball am Sempachersee"}
        title={settings?.heroTitel      || "Hier wächst die nächste Generation"}
        text={settings?.heroText        || "Das Team Sempachersee fördert talentierte Junioren aus der Region – sportlich, fair und mit Leidenschaft."}
        ctaLabel={settings?.heroCtaLabel || "Unsere Teams"}
        ctaTo={settings?.heroCtaTo      || "/teams"}
        bgImage={heroBgImage}
      />

      {/* Werte */}
      <section className="bg-light">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <SectionHeader
            eyebrow="Was uns ausmacht"
            title="Mehr als Fussball"
            text="Das TSS steht für positive Werte und bestmögliche Voraussetzungen für talentierte Junioren rund um den Sempachersee."
          />
          <div className="grid gap-6 sm:grid-cols-3">
            {werte.map((w) => (
              <div
                key={w.titel}
                className="bg-white p-7 border border-black/5 shadow-sm hover:shadow-md transition-shadow group"
              >
                <div className="inline-flex h-12 w-12 items-center justify-center bg-primary/10 text-primary mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                  {w.icon}
                </div>
                <h3 className="text-lg font-bold uppercase">{w.titel}</h3>
                <p className="mt-2 text-sm text-dark/70 leading-relaxed">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trägervereine */}
      <section className="mx-auto max-w-6xl px-4 py-16">
        <SectionHeader eyebrow="Die Basis" title="5 Trägervereine" />
        <TraegervereineGrid vereine={traegervereine} />
      </section>

      {/* News-Preview */}
      <section className="bg-light">
        <div className="mx-auto max-w-6xl px-4 py-20">
          <div className="flex items-end justify-between mb-10 gap-4">
            <SectionHeader eyebrow="Aktuell" title="Neueste News" />
            <Link
              to="/news"
              className="flex-shrink-0 text-sm font-bold uppercase tracking-wide text-primary hover:text-accent transition-colors flex items-center gap-1"
            >
              Alle News
              <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
              </svg>
            </Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {latestNews.map((artikel) => (
              <NewsCard key={artikel.id} artikel={artikel} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Sponsoring */}
      <CtaSection
        title="Werde Unterstützer"
        text="Sponsoring beim TSS verbindet Ihr Unternehmen mit einem erfolgreichen Nachwuchsprojekt. Leistung und Gegenleistung auf Augenhöhe."
        ctaLabel="Sponsoring anfragen"
        ctaTo="/kontakt"
      />
    </>
  );
}
